// Phase 4 design tokens for CookChow.
//
// This is the ONLY file in `apps/web/src/` permitted to contain raw hex
// literals. ESLint (see eslint.config.js) enforces this rule for the rest
// of `apps/web/src/`. Components must import colour from `./theme.ts`,
// which selects one palette per surface from the constants below.
//
// Two palettes co-exist:
//
// - `optionA` — the brief-literal palette (black/white banner, bright
//   orange completed, thick red timeline). Honoured for stakeholder
//   compliance. In v1 only `overrunBanner` is shipped from this palette;
//   the rest is retained for a future flag-flip.
//
// - `optionB` — the recommended calm palette per README §7.1. Shipped on
//   every other surface in v1.
//
// See README §7.1 (palette), §7.4 (palette tension), §6.7.4 (task
// rectangle colour states).

export const optionA = {
  // Black/white overrun banner with a stark count badge.
  overrunBanner: {
    bg: '#000000',
    text: '#FFFFFF',
    badge: '#FFFFFF',
    badgeText: '#000000',
  },
  // Brief-literal completed task: bright orange fill with magenta text.
  // Retained for a future flag flip; not shipped to TaskRectangle in v1.
  completed: {
    bg: '#FF7A1A',
    text: '#D6006C',
  },
  // Brief-literal timeline: pure red. Retained for a future flag flip;
  // not shipped to Timeline in v1.
  timeline: {
    band: '#FF0000',
  },
} as const;

// Lane chip colours, keyed by course. Fixed across every scheme (a lane is
// a semantic course key, not a palette surface) — like the Option-A overrun
// banner above. Each `fg` is chosen for legible contrast on its `bg`.
//   Alert → red, Starter → light blue, Main → gold, Dessert → teal.
export const lane = {
  alert: { bg: '#A93226', fg: '#FFFFFF' },
  starter: { bg: '#AED6F1', fg: '#1B2A38' },
  main: { bg: '#E0A82E', fg: '#2A2410' },
  dessert: { bg: '#117A65', fg: '#FFFFFF' },
} as const;

// Third-party brand colours — fixed across every scheme (a brand colour is
// owned by the third party, not the palette). The Ko-fi values configure the
// floating "Support Us" overlay widget (kofiOverlay.ts): button background +
// text colour, matching Ko-fi's own theming options.
export const brand = {
  kofi: { bg: '#fcbf47', fg: '#323842' },
} as const;

export const optionB = {
  bg: {
    base: '#F7F4EE',
    raised: '#E8DFCF',
    muted: '#E5E2DC',
  },
  accent: {
    calm: '#A8B5A0',
    action: '#C97B5C',
    warn: '#D6A24E',
    late: '#B5524A',
  },
  text: {
    primary: '#2A2A2A',
    muted: '#5C5A55',
  },
  // Pure-white surface; used only for high-contrast micro-elements like
  // a chip foreground on a coloured pill.
  surface: {
    onAccent: '#FFFFFF',
  },
} as const;

// --- Alternative colour schemes ------------------------------------------
//
// Same shape as `optionB`; chosen at runtime via the colour toggle. All are
// light-based (v1). The OutstandingPanel keeps Option A (black/white) in
// every scheme. See README §7.1 / §7.4.

// Monochrome and stark — the only colour is the alarm.
export const silhouette = {
  bg: { base: '#FAFAFA', raised: '#ECECEC', muted: '#DCDCDC' },
  accent: { calm: '#B8B8B8', action: '#222222', warn: '#777777', late: '#8A2B2B' },
  text: { primary: '#1A1A1A', muted: '#5A5A5A' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Vivid, maximally legible state colours.
export const distinct = {
  bg: { base: '#FBFBFC', raised: '#EEF1F5', muted: '#E2E6EB' },
  accent: { calm: '#3FA76A', action: '#2D6CDF', warn: '#E0A100', late: '#D64545' },
  text: { primary: '#1F2430', muted: '#566070' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Playful pastels — soft orchid, seafoam, gold (matched-lightness pastels).
export const whimsical = {
  bg: { base: '#FBF5FA', raised: '#F2E7F1', muted: '#E9DAE7' },
  accent: { calm: '#8FCDB8', action: '#C977A8', warn: '#E2B86A', late: '#D87596' },
  text: { primary: '#342D34', muted: '#6E6370' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Premium, "fine-dining" — ivory, forest, brass.
export const master = {
  bg: { base: '#F7F3EA', raised: '#ECE4D2', muted: '#E2D8C2' },
  accent: { calm: '#6E8B6F', action: '#9C7C38', warn: '#C28A2C', late: '#A4453A' },
  text: { primary: '#2B2A24', muted: '#5E594C' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Cool and fresh — ocean blue, teal, coral.
export const coastal = {
  bg: { base: '#F4F8FA', raised: '#E1ECF1', muted: '#D5E2E8' },
  accent: { calm: '#4FA890', action: '#2E7DA1', warn: '#D9A24A', late: '#C75E50' },
  text: { primary: '#213139', muted: '#566872' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Warm autumn — pumpkin, olive, mustard.
export const harvest = {
  bg: { base: '#FBF6EC', raised: '#F0E4CE', muted: '#E6D9BE' },
  accent: { calm: '#8B9B52', action: '#C9742C', warn: '#D2A02A', late: '#B0472F' },
  text: { primary: '#2E2A20', muted: '#6A6048' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Orchard berries — plum, teal-green, raspberry.
export const berry = {
  bg: { base: '#FAF4F8', raised: '#EEE0EA', muted: '#E4D4E0' },
  accent: { calm: '#4F9E86', action: '#8E4585', warn: '#C58A3E', late: '#BC4A63' },
  text: { primary: '#2C2530', muted: '#6A5E68' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Bright and zesty — tangerine, lime, lemon.
export const citrus = {
  bg: { base: '#FDFBEF', raised: '#F4EFD0', muted: '#EBE5C0' },
  accent: { calm: '#7DB23F', action: '#E08A1E', warn: '#E3C02A', late: '#D2552E' },
  text: { primary: '#2C2A1C', muted: '#66614A' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Cool neutral — slate blue, teal-green, gold.
export const slate = {
  bg: { base: '#F4F6F8', raised: '#E4E9EE', muted: '#D9DFE6' },
  accent: { calm: '#5C9C86', action: '#41698C', warn: '#C0903E', late: '#B05249' },
  text: { primary: '#232A31', muted: '#586470' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Soft violet — lilac, sage-teal, rose.
export const lavender = {
  bg: { base: '#F8F5FD', raised: '#ECE4F6', muted: '#E1D7EF' },
  accent: { calm: '#7FB0A0', action: '#7E63C0', warn: '#C99A4C', late: '#C25E86' },
  text: { primary: '#2D2836', muted: '#645C72' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Deep woodland — forest greens and clay.
export const forest = {
  bg: { base: '#F3F7F2', raised: '#E0EBDE', muted: '#D4E1D2' },
  accent: { calm: '#6FA877', action: '#2F6B4F', warn: '#C2922E', late: '#B0503A' },
  text: { primary: '#232B25', muted: '#566057' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Dusty pink — rose, sage, red.
export const rose = {
  bg: { base: '#FCF5F6', raised: '#F2E2E5', muted: '#E9D6DA' },
  accent: { calm: '#93B091', action: '#C56F86', warn: '#D2A24E', late: '#C04A57' },
  text: { primary: '#2F272A', muted: '#6E5F63' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Deep cool blue — ocean, teal, coral.
export const ocean = {
  bg: { base: '#F2F7F8', raised: '#DCEAEC', muted: '#CFE0E3' },
  accent: { calm: '#3F9E8C', action: '#1F6F8B', warn: '#DDA23B', late: '#C2544B' },
  text: { primary: '#1E2C30', muted: '#566A6E' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Warm browns — caramel, olive, brick.
export const mocha = {
  bg: { base: '#FAF6F0', raised: '#EBE0D0', muted: '#E1D4C0' },
  accent: { calm: '#8B9E66', action: '#8A5A3B', warn: '#C68E3A', late: '#A8503C' },
  text: { primary: '#2D261F', muted: '#6A5E50' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Coral and amber — a warm sunset with a warm olive completed.
export const sunset = {
  bg: { base: '#FFF6F0', raised: '#FAE6D9', muted: '#F1D7C8' },
  accent: { calm: '#8FAE5E', action: '#DE6646', warn: '#E6A63C', late: '#D2486A' },
  text: { primary: '#33251F', muted: '#6E594E' },
  surface: { onAccent: '#FFFFFF' },
} as const;

// Registry of selectable palettes. `calm` is `optionB` itself so existing
// reference-equality expectations (design/theme.test) still hold.
export const palettes = {
  calm: optionB,
  silhouette,
  distinct,
  whimsical,
  master,
  coastal,
  harvest,
  berry,
  citrus,
  slate,
  lavender,
  forest,
  rose,
  ocean,
  mocha,
  sunset,
} as const;

export type SchemeName = keyof typeof palettes;

export const motion = {
  duration: {
    fast: '200ms',
    medium: '250ms',
    slow: '300ms',
  },
  easing: 'ease-out',
  // The matchMedia query body. Components key animations off
  // `@media (prefers-reduced-motion: reduce)` in CSS and the runtime
  // equivalent in JS.
  reducedMotion: 'reduce',
} as const;

export type OptionAPalette = typeof optionA;
export type OptionBPalette = typeof optionB;
export type MotionTokens = typeof motion;
